<template>
    <div>
        <SetupPage
            parent-label="Staff Management"
            parent-path="/school/staff-management"
            page-label="Staff Profile"
            eyebrow=""
            title=""
            description=""
            toolbar-title="Staff records"
            :meta="directoryMeta"
            :loader="pageLoader"
        >
            <template slot="side">
                <div class="staff-actions">
                    <div v-if="staffs !== null" class="ws-stat">
                        <strong>{{ activeTab === 'deleted' ? deletedStaffs.length : activeStaffs.length }}</strong>
                        <span>{{ activeTab === 'deleted' ? 'deleted staff' : 'active staff' }}</span>
                    </div>
                    <button type="button" class="btn ws-primary-button" @click="OpenNewStaffModal">
                        <i class="fa fa-plus" aria-hidden="true"></i>
                        <span>Add staff</span>
                    </button>
                    <button type="button" class="btn ws-ghost-button" @click="openBulkUploadModal">
                        <i class="fa fa-file-excel-o" aria-hidden="true"></i>
                        <span>Bulk upload</span>
                    </button>
                    <button
                        v-if="pendingSyncCount"
                        type="button"
                        class="btn ws-ghost-button"
                        :disabled="syncingPending"
                        @click="pushPendingStaffSync"
                    >
                        <i
                            class="fa"
                            :class="syncingPending ? 'fa-spinner fa-spin' : 'fa-cloud-upload'"
                            aria-hidden="true"
                        ></i>
                        <span>Sync pending ({{ pendingSyncCount }})</span>
                    </button>
                    <button
                        type="button"
                        class="btn ws-ghost-button"
                        :disabled="!searchedResultQuery.length"
                        @click="downloadStaffList"
                    >
                        <i class="fa fa-download" aria-hidden="true"></i>
                        <span>Download</span>
                    </button>
                </div>
            </template>

            <template slot="toolbar">
                <div class="staff-tabs" role="tablist" aria-label="Staff status">
                    <button
                        type="button"
                        role="tab"
                        class="staff-tab"
                        :class="{ 'is-active': activeTab === 'active' }"
                        :aria-selected="activeTab === 'active' ? 'true' : 'false'"
                        @click="selectTab('active')"
                    >
                        Active
                        <span class="staff-tab__count">{{ activeStaffs.length }}</span>
                    </button>
                    <button
                        type="button"
                        role="tab"
                        class="staff-tab"
                        :class="{ 'is-active': activeTab === 'deleted' }"
                        :aria-selected="activeTab === 'deleted' ? 'true' : 'false'"
                        @click="selectTab('deleted')"
                    >
                        Deleted
                        <span class="staff-tab__count">{{ deletedStaffs.length }}</span>
                    </button>
                </div>

                <div class="ws-search">
                    <i class="fa fa-search" aria-hidden="true"></i>
                    <input
                        v-model="searchval"
                        type="search"
                        placeholder="Search name, service no. or email"
                        aria-label="Search staff records"
                        :disabled="staffs === null"
                    />
                    <button
                        v-if="searchval"
                        type="button"
                        class="ws-search__clear"
                        aria-label="Clear search"
                        @click="searchval = ''"
                    >
                        <i class="fa fa-times" aria-hidden="true"></i>
                    </button>
                </div>
            </template>

            <div v-if="staffs === null" class="staff-grid" aria-busy="true">
                <staff-card-skeleton v-for="n in 4" :key="`skeleton-${n}`" />
            </div>

            <div v-else-if="!searchedResultQuery.length" class="ws-state">
                <i class="fa" :class="searchval.trim() ? 'fa-search' : 'fa-users'" aria-hidden="true"></i>
                <strong v-if="searchval.trim()">No staff match “{{ searchval.trim() }}”</strong>
                <strong v-else-if="activeTab === 'deleted'">No deleted staff</strong>
                <strong v-else>No staff records yet</strong>
                <span v-if="searchval.trim()">Try a different name, service number or email address.</span>
                <span v-else-if="activeTab === 'deleted'">Deleted staff will appear here and can be restored.</span>
                <span v-else>Add the first staff member to start building the directory.</span>
                <button
                    v-if="searchval.trim()"
                    type="button"
                    class="btn ws-ghost-button"
                    @click="searchval = ''"
                >
                    Clear search
                </button>
                <button
                    v-else-if="activeTab === 'deleted'"
                    type="button"
                    class="btn ws-ghost-button"
                    @click="selectTab('active')"
                >
                    View active staff
                </button>
                <button v-else type="button" class="btn ws-ghost-button" @click="OpenNewStaffModal">
                    <i class="fa fa-plus" aria-hidden="true"></i>
                    <span>Add staff</span>
                </button>
            </div>

            <div v-else class="staff-grid">
                <staff-card
                    v-for="item in searchedResultQuery"
                    :key="item.id"
                    :item="item"
                    @edit="EditStaffDetails"
                    @toggle-delete="DeleteSchoolStaff"
                    @add-class="addClassTeacher"
                    @add-subject="addSubjectTeacher"
                    @delete-class="deleteclassteacher"
                    @delete-subject="deletesubjectteacher"
                    @discard-pending="DiscardPendingStaff"
                />
            </div>
        </SetupPage>

        <SetupDrawer
            :show.sync="staffDrawerVisible"
            icon="fa-user-plus"
            :eyebrow="isModified ? 'UPDATE RECORD' : 'NEW RECORD'"
            :title="isModified ? 'Edit staff profile' : 'Add a staff member'"
            :subtitle="staffDrawerSubtitle"
            :saving="staffSaving"
            :submit-label="isModified ? 'Save changes' : 'Create staff'"
            form-id="staff-form"
            @close="CloseStaffForm"
            @submit="SubmitStaffDetails"
        >
            <fieldset class="ws-fieldset">
                <legend>Photo &amp; identity</legend>

                <div class="staff-photo">
                    <div class="staff-photo__preview">
                        <img
                            :src="ImgPathval || '/img/brand/empty.png'"
                            alt="Staff photo preview"
                        />
                    </div>
                    <div class="staff-photo__upload">
                        <ImageUploader
                            :existing-path="ImgPathval"
                            @emitOutImgPathval="updatePassportVal"
                        />
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-title">Title <span class="ws-required">*</span></label>
                            <input
                                id="staff-title"
                                v-model.trim="TitleVal"
                                type="text"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.title }"
                                maxlength="20"
                                autocomplete="honorific-prefix"
                                @input="clearStaffError('title')"
                            />
                            <span v-if="staffErrors.title" class="ws-error" role="alert">
                                {{ staffErrors.title }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-last-name">Surname <span class="ws-required">*</span></label>
                            <input
                                id="staff-last-name"
                                v-model.trim="LastNameVal"
                                type="text"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.lastName }"
                                maxlength="80"
                                autocomplete="family-name"
                                @input="clearStaffError('lastName')"
                            />
                            <span v-if="staffErrors.lastName" class="ws-error" role="alert">
                                {{ staffErrors.lastName }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-first-name">First name <span class="ws-required">*</span></label>
                            <input
                                id="staff-first-name"
                                v-model.trim="FirstNameVal"
                                type="text"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.firstName }"
                                maxlength="80"
                                autocomplete="given-name"
                                @input="clearStaffError('firstName')"
                            />
                            <span v-if="staffErrors.firstName" class="ws-error" role="alert">
                                {{ staffErrors.firstName }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-middle-name">
                                Other name <span class="ws-required">*</span>
                            </label>
                            <input
                                id="staff-middle-name"
                                v-model.trim="MiddleNameVal"
                                type="text"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.middleName }"
                                maxlength="80"
                                autocomplete="additional-name"
                                @input="clearStaffError('middleName')"
                            />
                            <span v-if="staffErrors.middleName" class="ws-error" role="alert">
                                {{ staffErrors.middleName }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-sex">Sex <span class="ws-required">*</span></label>
                            <select
                                id="staff-sex"
                                v-model="SexIdVal"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.sexId }"
                                @change="clearStaffError('sexId')"
                            >
                                <option :value="0">Select sex</option>
                                <option :value="10">Male</option>
                                <option :value="11">Female</option>
                            </select>
                            <span v-if="staffErrors.sexId" class="ws-error" role="alert">
                                {{ staffErrors.sexId }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-marital">
                                Marital status <span class="ws-required">*</span>
                            </label>
                            <select
                                id="staff-marital"
                                v-model="MaritalStatusIdVal"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.maritalStatusId }"
                                @change="clearStaffError('maritalStatusId')"
                            >
                                <option :value="0">Select marital status</option>
                                <option :value="12">Married</option>
                                <option :value="13">Single</option>
                            </select>
                            <span v-if="staffErrors.maritalStatusId" class="ws-error" role="alert">
                                {{ staffErrors.maritalStatusId }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-dob">
                                Date of birth <span class="ws-required">*</span>
                            </label>
                            <input
                                id="staff-dob"
                                v-model="DOBval"
                                type="date"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.dob }"
                                :max="getTodayDate"
                                autocomplete="bday"
                                @input="clearStaffError('dob')"
                            />
                            <span v-if="staffErrors.dob" class="ws-error" role="alert">
                                {{ staffErrors.dob }}
                            </span>
                        </div>
                    </div>
                </div>
            </fieldset>

            <fieldset class="ws-fieldset">
                <legend>Contact</legend>
                <div class="row">
                    <div class="col-sm-6">
                        <div class="form-group">
                            <label for="staff-email">Email address <span class="ws-required">*</span></label>
                            <input
                                id="staff-email"
                                v-model.trim="EmailAddressVal"
                                type="email"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.email }"
                                maxlength="254"
                                autocomplete="email"
                                @input="clearStaffError('email')"
                            />
                            <span v-if="staffErrors.email" class="ws-error" role="alert">
                                {{ staffErrors.email }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-6">
                        <div class="form-group">
                            <label for="staff-phone">
                                Phone number <span class="ws-required">*</span>
                            </label>
                            <input
                                id="staff-phone"
                                v-model.trim="PhoneNoVal"
                                type="tel"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.phone }"
                                maxlength="20"
                                autocomplete="tel"
                                @input="clearStaffError('phone')"
                            />
                            <span v-if="staffErrors.phone" class="ws-error" role="alert">
                                {{ staffErrors.phone }}
                            </span>
                        </div>
                    </div>

                    <div class="col-12">
                        <div class="form-group">
                            <label for="staff-address">Full address <span class="ws-required">*</span></label>
                            <textarea
                                id="staff-address"
                                v-model.trim="FullAddressVal"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.fullAddress }"
                                rows="3"
                                maxlength="500"
                                autocomplete="street-address"
                                @input="clearStaffError('fullAddress')"
                            ></textarea>
                            <span v-if="staffErrors.fullAddress" class="ws-error" role="alert">
                                {{ staffErrors.fullAddress }}
                            </span>
                        </div>
                    </div>
                </div>
            </fieldset>

            <fieldset class="ws-fieldset">
                <legend>Employment</legend>
                <div class="row">
                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-service-no">Service number <span class="ws-required">*</span></label>
                            <input
                                id="staff-service-no"
                                v-model.trim="ServiceNoVal"
                                type="text"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.serviceNo }"
                                maxlength="50"
                                @input="clearStaffError('serviceNo')"
                            />
                            <span v-if="staffErrors.serviceNo" class="ws-error" role="alert">
                                {{ staffErrors.serviceNo }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-category">Staff type <span class="ws-required">*</span></label>
                            <select
                                id="staff-category"
                                v-model="CategoryIdVal"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.categoryId }"
                                @change="clearStaffError('categoryId')"
                            >
                                <option :value="0">Select staff type</option>
                                <option :value="14">Teaching</option>
                                <option :value="15">Non-Teaching</option>
                            </select>
                            <span v-if="staffErrors.categoryId" class="ws-error" role="alert">
                                {{ staffErrors.categoryId }}
                            </span>
                        </div>
                    </div>

                    <div class="col-sm-4">
                        <div class="form-group">
                            <label for="staff-password">
                                Password
                                <span v-if="!isModified || editingPendingCreate" class="ws-required">*</span>
                            </label>
                            <input
                                id="staff-password"
                                v-model="UserPasswordVal"
                                type="password"
                                class="form-control"
                                :class="{ 'is-invalid': staffErrors.password }"
                                minlength="8"
                                autocomplete="new-password"
                                @input="clearStaffError('password')"
                            />
                            <span v-if="staffErrors.password" class="ws-error" role="alert">
                                {{ staffErrors.password }}
                            </span>
                            <span v-else class="ws-hint">
                                {{ isModified && !editingPendingCreate ? 'Leave blank to keep the current password.' : 'At least 8 characters.' }}
                            </span>
                        </div>
                    </div>
                </div>
            </fieldset>

            <fieldset class="ws-fieldset">
                <legend>Access roles</legend>
                <p class="ws-fieldset__note">
                    Tick every additional responsibility this staff member should have.
                </p>
                <div class="staff-roles">
                    <base-checkbox v-model="IsBursalVal">Bursar</base-checkbox>
                    <base-checkbox v-model="IsCounselorVal">Counselor</base-checkbox>
                    <base-checkbox v-model="IsHODVal">HOD</base-checkbox>
                    <base-checkbox v-model="IsHostelMasterVal">Hostel Master / Mistress</base-checkbox>
                    <base-checkbox v-model="IsLibraryAdminVal">Library Admin</base-checkbox>
                    <base-checkbox v-model="IsRegistrarVal">Registrar</base-checkbox>
                    <base-checkbox v-model="IsDailyAttendanceManagerVal">Daily Attendance Manager</base-checkbox>
                    <base-checkbox v-model="IsPortalAdminVal">Portal Admin</base-checkbox>
                    <base-checkbox v-model="IsPrincipalVal">Principal</base-checkbox>
                </div>
            </fieldset>
        </SetupDrawer>

        <SetupDrawer
            :show.sync="classTeacherVisible"
            icon="fa-chalkboard-teacher"
            eyebrow="CLASS ASSIGNMENT"
            title="Assign a class"
            subtitle="Choose the class, option and arm this staff member leads."
            :saving="classTeacherSaving"
            submit-label="Assign class"
            form-id="class-teacher-form"
            @close="closeClassTeacher"
            @submit="SubmitAssignedClass"
        >
            <div class="assign-context">
                <img :src="staffPassport || '/img/brand/loguser.png'" alt="" />
                <div>
                    <strong>{{ staffName || "Selected staff member" }}</strong>
                    <span>Class teacher assignment</span>
                </div>
            </div>

            <fieldset class="ws-fieldset">
                <legend>Class details</legend>
                <div class="form-group">
                    <label for="assign-class">Class <span class="ws-required">*</span></label>
                    <select
                        id="assign-class"
                        v-model="ClassIdvalAssignClass"
                        class="form-control"
                        :class="{ 'is-invalid': classTeacherErrors.classId }"
                        @change="getOptionForAssignClass"
                    >
                        <option :value="0">Select class</option>
                        <option v-for="item in classes" :key="item.Id" :value="item.Id">
                            {{ item.Sch_Class }}
                        </option>
                    </select>
                    <span v-if="classTeacherErrors.classId" class="ws-error" role="alert">
                        {{ classTeacherErrors.classId }}
                    </span>
                </div>

                <div class="form-group">
                    <label for="assign-option">Option <span class="ws-required">*</span></label>
                    <select
                        id="assign-option"
                        v-model="OptionIdvalAssignClass"
                        class="form-control"
                        :class="{ 'is-invalid': classTeacherErrors.optionId }"
                        :disabled="!ClassIdvalAssignClass || classOptionsLoading"
                        @change="getArmForAssignClass"
                    >
                        <option v-if="classOptionsLoading" :value="0" disabled>Loading options…</option>
                        <option v-else :value="0">Select option</option>
                        <option v-for="item in classOptions" :key="item.Id" :value="item.Id">
                            {{ item.ClassOption }}
                        </option>
                    </select>
                    <span v-if="classTeacherErrors.optionId" class="ws-error" role="alert">
                        {{ classTeacherErrors.optionId }}
                    </span>
                </div>

                <div class="form-group">
                    <label for="assign-arm">Arm <span class="ws-required">*</span></label>
                    <select
                        id="assign-arm"
                        v-model="ArmIdvalAssignClass"
                        class="form-control"
                        :class="{ 'is-invalid': classTeacherErrors.armId }"
                        :disabled="!OptionIdvalAssignClass || classArmsLoading"
                    >
                        <option v-if="classArmsLoading" :value="0" disabled>Loading arms…</option>
                        <option v-else :value="0">Select arm</option>
                        <option v-for="item in classArms" :key="item.Id" :value="item.Id">
                            {{ item.SectionName }}
                        </option>
                    </select>
                    <span v-if="classTeacherErrors.armId" class="ws-error" role="alert">
                        {{ classTeacherErrors.armId }}
                    </span>
                </div>
            </fieldset>
        </SetupDrawer>

        <SetupDrawer
            :show.sync="subjectTeacherVisible"
            icon="fa-book"
            eyebrow="SUBJECT ASSIGNMENT"
            title="Assign a subject"
            subtitle="Choose the class, option and subject this staff member teaches."
            :saving="subjectTeacherSaving"
            submit-label="Assign subject"
            form-id="subject-teacher-form"
            @close="closeSubjectTeacher"
            @submit="SubmitSubjectTeacher"
        >
            <div class="assign-context">
                <img :src="staffPassport || '/img/brand/loguser.png'" alt="" />
                <div>
                    <strong>{{ staffName || "Selected staff member" }}</strong>
                    <span>Subject teacher assignment</span>
                </div>
            </div>

            <fieldset class="ws-fieldset">
                <legend>Subject details</legend>
                <div class="form-group">
                    <label for="subject-class">Class <span class="ws-required">*</span></label>
                    <select
                        id="subject-class"
                        v-model="ClassIdvalSubjectTeacher"
                        class="form-control"
                        :class="{ 'is-invalid': subjectTeacherErrors.classId }"
                        @change="getOptionForSubjectTeacher"
                    >
                        <option :value="0">Select class</option>
                        <option v-for="item in classes" :key="item.Id" :value="item.Id">
                            {{ item.Sch_Class }}
                        </option>
                    </select>
                    <span v-if="subjectTeacherErrors.classId" class="ws-error" role="alert">
                        {{ subjectTeacherErrors.classId }}
                    </span>
                </div>

                <div class="form-group">
                    <label for="subject-option">Option <span class="ws-required">*</span></label>
                    <select
                        id="subject-option"
                        v-model="OptionIdvalSubjectTeacher"
                        class="form-control"
                        :class="{ 'is-invalid': subjectTeacherErrors.optionId }"
                        :disabled="!ClassIdvalSubjectTeacher || subjectOptionsLoading"
                        @change="getschsubject"
                    >
                        <option v-if="subjectOptionsLoading" :value="0" disabled>Loading options…</option>
                        <option v-else :value="0">Select option</option>
                        <option v-for="item in subjectOptions" :key="item.Id" :value="item.Id">
                            {{ item.ClassOption }}
                        </option>
                    </select>
                    <span v-if="subjectTeacherErrors.optionId" class="ws-error" role="alert">
                        {{ subjectTeacherErrors.optionId }}
                    </span>
                </div>

                <div class="form-group">
                    <label for="subject-name">Subject <span class="ws-required">*</span></label>
                    <select
                        id="subject-name"
                        v-model="SubjectIdval"
                        class="form-control"
                        :class="{ 'is-invalid': subjectTeacherErrors.subjectId }"
                        :disabled="!OptionIdvalSubjectTeacher || subjectSubjectsLoading"
                    >
                        <option v-if="subjectSubjectsLoading" :value="0" disabled>Loading subjects…</option>
                        <option v-else :value="0">Select subject</option>
                        <option v-for="item in subjectSubjects" :key="item.Id" :value="item.Id">
                            {{ item.SchSubject }}
                        </option>
                    </select>
                    <span v-if="subjectTeacherErrors.subjectId" class="ws-error" role="alert">
                        {{ subjectTeacherErrors.subjectId }}
                    </span>
                </div>
            </fieldset>
        </SetupDrawer>

        <modal
            :show.sync="modals.bulkUploadModal"
            modal-classes="modal-dialog-centered modal-xl"
            modal-content-classes="bulk-upload-modal-content"
        >
            <div class="staff-bulk">
                <header class="staff-bulk__head">
                    <div>
                        <span class="staff-bulk__eyebrow">STAFF MANAGEMENT</span>
                        <h3>Bulk staff upload</h3>
                        <p>Import staff records from a spreadsheet and queue them for synchronization.</p>
                    </div>
                    <button
                        type="button"
                        class="ws-drawer__close"
                        aria-label="Close"
                        @click="modals.bulkUploadModal = false"
                    >
                        <i class="fa fa-times" aria-hidden="true"></i>
                    </button>
                </header>

                <StaffBulkUpload
                    :staff-repository="staffRepoInstance"
                    :existing-staffs="staffs || []"
                    @staff-created="handleBulkStaffCreated"
                    @close="modals.bulkUploadModal = false"
                />
            </div>
        </modal>
    </div>
</template>

<script>
import moment from "moment";
import * as XLSX from "xlsx";
import Alert from "@/mixins/Alert.mixins";
import CheckStaffUser from "@/mixins/CheckStaffUser.mixin";
import ImageUploader from "@/components/ImageUploader";
import SetupPage from "@/components/SetupPage";
import SetupDrawer from "@/components/SetupDrawer";
import StaffCard from "./Component/StaffCard";
import StaffCardSkeleton from "./Component/StaffCardSkeleton";
import StaffBulkUpload from "./Component/StaffBulkUpload.vue";
import { getDB } from "@/rxdb";
import { createOfflineRepository } from "@/rxdb/repository";
import { subscribeSynced, syncOperations } from "@/rxdb/offlineSync";
import { getCurrentUserScope, getOfflineEntityId } from "@/rxdb/sessionScope";

const staffRepository = createOfflineRepository({
    entityType: "staff",
    url: (data, operation) => {
        const base = process.env.VUE_APP_ENV_API_URL || "";
        if (operation === "delete") {
            return `${base}PortalSchoolStaff/DeleteSchoolStaff`;
        }
        return operation === "create"
            ? `${base}PortalSchoolStaff/CreateSchoolStaff`
            : `${base}PortalSchoolStaff/UpdateSchoolStaff`;
    },
    method: () => "POST",
    dedupeKey: data => {
        const serviceNo = data && (data.seviceNo || data.serviceNo || data.ServiceNo);
        if (serviceNo) return `service:${String(serviceNo).trim().toLowerCase()}`;
        const email = data && (data.emailAddress || data.EmailAddress);
        return email ? `email:${String(email).trim().toLowerCase()}` : null;
    }
});

function displayStaff(staff) {
    const source = staff.data || staff;
    const firstName = source.firstName || source.FirstName || "";
    const middleName = source.middleName || source.MiddleName || "";
    const lastName = source.lastName || source.LastName || "";
    const title = source.title || source.Title || "";
    const staffName =
        source.staffName ||
        [title, firstName, middleName, lastName].filter(Boolean).join(" ") ||
        [firstName, lastName].filter(Boolean).join(" ");
    return {
        ...source,
        id: source.id || source.staffId || source.StaffId || source.clientId,
        staffName,
        seviceNo: source.seviceNo || source.serviceNo || source.ServiceNo || "",
        emailAddress: source.emailAddress || source.EmailAddress || "",
        phoneNumber:
            source.phoneNumber || source.phoneNo || source.PhoneNo || source.PhoneNumber || "",
        passport: source.passport || source.Passport || "",
        isDeleted: Boolean(source.isDeleted),
        classAssigned: source.classAssigned || [],
        subjectAssigned: source.subjectAssigned || [],
        syncStatus: staff.syncStatus || source.syncStatus || "synced"
    };
}

const EMPTY_ASSIGNMENT_ERRORS = () => ({
    classId: "",
    optionId: "",
    armId: "",
    subjectId: ""
});

export default {
    name: "staff",
    mixins: [CheckStaffUser, Alert],
    components: {
        ImageUploader,
        SetupPage,
        SetupDrawer,
        StaffCard,
        StaffCardSkeleton,
        StaffBulkUpload
    },
    data() {
        return {
            accessDetails: null,
            staffs: null,
            searchval: "",
            activeTab: "active",
            isModified: false,
            onloader: "none",

            IdVal: 0,
            ImgPathval: "",
            TitleVal: "",
            FirstNameVal: "",
            MiddleNameVal: "",
            LastNameVal: "",
            PhoneNoVal: "",
            EmailAddressVal: "",
            SexIdVal: 0,
            DOBval: "",
            FullAddressVal: "",
            ServiceNoVal: "",
            CategoryIdVal: 0,
            MaritalStatusIdVal: 0,
            UserPasswordVal: "",
            IsPrincipalVal: false,
            IsBursalVal: false,
            IsPortalAdminVal: false,
            IsHostelMasterVal: false,
            IsLibraryAdminVal: false,
            IsCounselorVal: false,
            IsRegistrarVal: false,
            IsHODVal: false,
            IsDailyAttendanceManagerVal: false,
            staffErrors: {},
            staffDrawerVisible: false,
            staffSaving: false,
            syncingPending: false,
            staffLoadCount: 0,
            editingPending: false,
            editingPendingCreate: false,
            editingClientId: "",

            StaffIdval: 0,
            staffName: "",
            staffPassport: "",
            schclass: null,

            classTeacherVisible: false,
            classTeacherSaving: false,
            classTeacherErrors: EMPTY_ASSIGNMENT_ERRORS(),
            ClassIdvalAssignClass: 0,
            OptionIdvalAssignClass: 0,
            ArmIdvalAssignClass: 0,
            classOptions: [],
            classArms: [],
            classOptionsLoading: false,
            classArmsLoading: false,

            subjectTeacherVisible: false,
            subjectTeacherSaving: false,
            subjectTeacherErrors: EMPTY_ASSIGNMENT_ERRORS(),
            ClassIdvalSubjectTeacher: 0,
            OptionIdvalSubjectTeacher: 0,
            SubjectIdval: 0,
            subjectOptions: [],
            subjectSubjects: [],
            subjectOptionsLoading: false,
            subjectSubjectsLoading: false,

            modals: {
                bulkUploadModal: false
            }
        };
    },
    computed: {
        staffRepoInstance() {
            return staffRepository;
        },
        pageLoader() {
            return this.staffLoadCount > 0 || this.onloader === "flex" ? "flex" : "none";
        },
        getTodayDate() {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, "0");
            const day = String(today.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        },
        classes() {
            return Array.isArray(this.schclass) ? this.schclass : [];
        },
        activeStaffs() {
            if (!this.staffs) return [];
            return this.staffs.filter(item => !item.isDeleted);
        },
        deletedStaffs() {
            if (!this.staffs) return [];
            return this.staffs.filter(item => item.isDeleted);
        },
        pendingSyncCount() {
            if (!this.staffs) return 0;
            return this.staffs.filter(item =>
                ["pending", "failed"].includes(item.syncStatus)
            ).length;
        },
        searchedResultQuery() {
            if (!this.staffs) return [];
            const tabList = this.activeTab === "deleted" ? this.deletedStaffs : this.activeStaffs;
            const search = this.searchval.trim().toLowerCase();
            if (!search) return tabList;

            const tokens = search.split(/\s+/).filter(Boolean);
            return tabList.filter(item => {
                const haystack = [item.staffName, item.seviceNo, item.emailAddress, item.phoneNumber]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        directoryMeta() {
            if (this.staffs === null) return "Loading staff records…";
            const total = this.activeTab === "deleted" ? this.deletedStaffs.length : this.activeStaffs.length;
            const status = this.activeTab === "deleted" ? "deleted" : "active";
            const pending = this.pendingSyncCount
                ? ` · ${this.pendingSyncCount} pending sync`
                : "";
            if (this.searchval.trim()) {
                return `${this.searchedResultQuery.length} of ${total} shown · ${status}${pending}`;
            }
            return `${total} ${total === 1 ? "staff member" : "staff members"} · ${status}${pending}`;
        },
        staffDrawerSubtitle() {
            if (!this.isModified) return "Capture biodata, employment details and portal roles.";
            return (
                [this.TitleVal, this.FirstNameVal, this.LastNameVal].filter(Boolean).join(" ") ||
                "Update this staff record."
            );
        }
    },
    methods: {
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        selectTab(tab) {
            this.activeTab = tab;
            this.searchval = "";
        },
        downloadStaffList() {
            const assignedNames = (items, keys) =>
                (Array.isArray(items) ? items : [])
                    .map(item => {
                        if (typeof item === "string") return item;
                        return keys.map(key => item && item[key]).find(Boolean) || "";
                    })
                    .filter(Boolean)
                    .join(", ");
            const rows = this.searchedResultQuery.map(staff => ({
                "Staff ID": staff.id || staff.staffId || staff.StaffId || "",
                "Staff Name": staff.staffName || "",
                Title: staff.title || staff.Title || "",
                "First Name": staff.firstName || staff.FirstName || "",
                "Middle Name": staff.middleName || staff.MiddleName || "",
                "Last Name": staff.lastName || staff.LastName || "",
                "Service Number": staff.seviceNo || staff.serviceNo || staff.ServiceNo || "",
                Email: staff.emailAddress || staff.EmailAddress || "",
                Phone: staff.phoneNumber || staff.PhoneNumber || staff.phoneNo || staff.PhoneNo || "",
                Gender:
                    Number(staff.sexId || staff.SexId) === 10
                        ? "Male"
                        : Number(staff.sexId || staff.SexId) === 11
                        ? "Female"
                        : staff.gender || staff.Gender || "",
                "Date of Birth": staff.dateOfBirth || staff.DateOfBirth || staff.DOB || "",
                Address: staff.fullAddress || staff.FullAddress || "",
                "Staff Type":
                    Number(staff.categoryId || staff.CategoryId) === 14
                        ? "Teaching"
                        : Number(staff.categoryId || staff.CategoryId) === 15
                        ? "Non-Teaching"
                        : staff.staffType || "",
                "Assigned Classes": assignedNames(staff.classAssigned, [
                    "className",
                    "ClassName",
                    "Sch_Class",
                    "classOption",
                    "ClassOption"
                ]),
                "Assigned Subjects": assignedNames(staff.subjectAssigned, [
                    "subjectName",
                    "SubjectName",
                    "SchSubject",
                    "subject",
                    "Subject"
                ]),
                Principal: Boolean(staff.isPrincipal || staff.IsPrincipal),
                Bursar: Boolean(staff.isBursal || staff.IsBursal),
                "Portal Admin": Boolean(staff.isPortalAdmin || staff.IsPortalAdmin),
                Counselor: Boolean(staff.isCounselor || staff.IsCounselor),
                "Hostel Master": Boolean(staff.isHostelMaster || staff.IsHostelMaster),
                "Library Admin": Boolean(staff.isLibraryAdmin || staff.IsLibraryAdmin),
                Registrar: Boolean(staff.isRegistrar || staff.IsRegistrar),
                HOD: Boolean(staff.isHOD || staff.IsHOD),
                "Daily Attendance Manager": Boolean(
                    staff.isDailyAttendanceManager || staff.IsDailyAttendanceManager
                ),
                Status: staff.isDeleted ? "Deleted" : "Active",
                "Sync Status": staff.syncStatus || "synced"
            }));

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "Staff");
            XLSX.writeFile(
                workbook,
                `${this.activeTab}-staff-${new Date().toISOString().slice(0, 10)}.xlsx`
            );
        },
        openBulkUploadModal() {
            this.modals.bulkUploadModal = true;
        },
        handleBulkStaffCreated(localStaff) {
            if (!localStaff) return;
            const staff = displayStaff({ ...localStaff, syncStatus: "pending" });
            const index = (this.staffs || []).findIndex(
                item =>
                    String(item.id) === String(staff.id) ||
                    (staff.emailAddress &&
                        String(item.emailAddress).trim().toLowerCase() ===
                            String(staff.emailAddress).trim().toLowerCase()) ||
                    (staff.seviceNo &&
                        String(item.seviceNo).trim().toLowerCase() ===
                            String(staff.seviceNo).trim().toLowerCase())
            );
            if (index >= 0) {
                this.$set(this.staffs, index, staff);
            } else {
                this.staffs = [staff, ...(this.staffs || [])];
            }
        },

        /* ---------- Staff form ---------- */

        resetStaffForm() {
            this.IdVal = 0;
            this.ImgPathval = "";
            this.TitleVal = "";
            this.FirstNameVal = "";
            this.MiddleNameVal = "";
            this.LastNameVal = "";
            this.PhoneNoVal = "";
            this.EmailAddressVal = "";
            this.SexIdVal = 0;
            this.DOBval = "";
            this.FullAddressVal = "";
            this.ServiceNoVal = "";
            this.CategoryIdVal = 0;
            this.MaritalStatusIdVal = 0;
            this.UserPasswordVal = "";
            this.IsPrincipalVal = false;
            this.IsBursalVal = false;
            this.IsPortalAdminVal = false;
            this.IsHostelMasterVal = false;
            this.IsLibraryAdminVal = false;
            this.IsCounselorVal = false;
            this.IsRegistrarVal = false;
            this.IsHODVal = false;
            this.IsDailyAttendanceManagerVal = false;
            this.staffErrors = {};
            this.isModified = false;
            this.editingPending = false;
            this.editingPendingCreate = false;
            this.editingClientId = "";
        },
        OpenNewStaffModal() {
            this.resetStaffForm();
            this.GetServiceNo();
            this.staffDrawerVisible = true;
        },
        CloseStaffForm() {
            this.staffDrawerVisible = false;
            this.resetStaffForm();
        },
        updatePassportVal(data) {
            this.ImgPathval = data;
        },
        pickValue(...values) {
            for (const value of values) {
                if (value !== undefined && value !== null) return value;
            }
            return "";
        },
        toDateInput(value) {
            if (!value) return "";
            const text = String(value);
            if (text.startsWith("0001-01-01")) return "";
            const parsed = moment(value);
            return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
        },
        findStaffRecord(staffOrId) {
            if (staffOrId && typeof staffOrId === "object") return staffOrId;
            return (
                (this.staffs || []).find(item => String(item.id) === String(staffOrId)) || null
            );
        },
        async EditStaffDetails(staffOrId) {
            const record = this.findStaffRecord(staffOrId);
            if (!record) {
                this.ShowAlertError(
                    "The selected staff record could not be found in the loaded list.",
                    ""
                );
                return;
            }

            this.resetStaffForm();
            this.editingPending = Boolean(
                record.syncStatus && record.syncStatus !== "synced"
            );
            this.editingClientId = record.clientId || record.id || "";
            if (this.editingPending) {
                await this.detectPendingOperation();
            }
            this.IdVal = record.id || record.staffId || record.StaffId || 0;
            this.ImgPathval = record.passport || record.Passport || "";
            this.TitleVal = record.title || record.Title || "";
            this.FirstNameVal = record.firstName || record.FirstName || "";
            this.MiddleNameVal = record.middleName || record.MiddleName || "";
            this.LastNameVal = record.lastName || record.LastName || "";
            this.PhoneNoVal = this.pickValue(
                record.phoneNumber,
                record.phoneNo,
                record.PhoneNo,
                record.PhoneNumber
            );
            this.EmailAddressVal = record.emailAddress || record.EmailAddress || "";
            this.SexIdVal = this.pickValue(record.sexId, record.SexId, 0);
            this.MaritalStatusIdVal = this.pickValue(
                record.maritalStatusId,
                record.MaritalStatusId,
                0
            );
            this.CategoryIdVal = this.pickValue(record.categoryId, record.CategoryId, 0);
            this.ServiceNoVal = record.seviceNo || record.serviceNo || record.ServiceNo || "";
            this.FullAddressVal = record.fullAddress || record.FullAddress || "";
            this.DOBval = this.toDateInput(
                this.pickValue(record.dob, record.DOB, record.dateOfBirth, record.DateOfBirth)
            );
            this.UserPasswordVal = "";

            this.IsPrincipalVal = Boolean(
                this.pickValue(record.isPrincipal, record.IsPrincipal, false)
            );
            this.IsBursalVal = Boolean(this.pickValue(record.isBursal, record.IsBursal, false));
            this.IsPortalAdminVal = Boolean(
                this.pickValue(record.isPortalAdmin, record.IsPortalAdmin, false)
            );
            this.IsHostelMasterVal = Boolean(
                this.pickValue(record.isHostelMaster, record.IsHostelMaster, false)
            );
            this.IsLibraryAdminVal = Boolean(
                this.pickValue(record.isLibraryAdmin, record.IsLibraryAdmin, false)
            );
            this.IsCounselorVal = Boolean(
                this.pickValue(record.isCounselor, record.IsCounselor, false)
            );
            this.IsRegistrarVal = Boolean(
                this.pickValue(record.isRegistrar, record.IsRegistrar, false)
            );
            this.IsHODVal = Boolean(
                this.pickValue(record.isHod, record.isHOD, record.IsHod, record.IsHOD, false)
            );
            this.IsDailyAttendanceManagerVal = Boolean(
                this.pickValue(
                    record.isDailyAttendanceManager,
                    record.IsDailyAttendanceManager,
                    false
                )
            );

            this.isModified = true;
            this.staffErrors = {};
            this.staffDrawerVisible = true;
        },
        async detectPendingOperation() {
            this.editingPendingCreate = true;
            try {
                const clientId = String(this.editingClientId || "").replace(/^staff:/, "");
                if (!clientId) return;

                const db = await getDB();
                const entityKey = getOfflineEntityId("staff", clientId);
                const operations = await db.syncOperations
                    .find({ selector: { entityKey } })
                    .exec();
                const operation =
                    operations.find(doc => {
                        const status = doc.toJSON().syncStatus;
                        return (
                            status === "pending" ||
                            status === "failed" ||
                            status === "syncing"
                        );
                    }) || operations[0];

                if (operation) {
                    this.editingPendingCreate = /CreateSchoolStaff/i.test(
                        String(operation.toJSON().url || "")
                    );
                    return;
                }

                const localDoc = await db.offlineEntities.findOne(entityKey).exec();
                if (localDoc) {
                    this.editingPendingCreate = localDoc.toJSON().operation !== "update";
                }
            } catch (error) {
                this.editingPendingCreate = true;
            }
        },
        clearStaffError(field) {
            if (!this.staffErrors[field]) return;
            const errors = { ...this.staffErrors };
            delete errors[field];
            this.staffErrors = errors;
        },
        showErrorAlert(errors) {
            const messages = Array.isArray(errors) ? errors : [errors];
            return this.ShowAlertError(messages.filter(Boolean).join("\n"), "");
        },
        validateStaffForm() {
            const errors = {};
            const required = value => String(value || "").trim();
            const email = required(this.EmailAddressVal);
            const phone = required(this.PhoneNoVal);
            const title = required(this.TitleVal);
            const firstName = required(this.FirstNameVal);
            const middleName = required(this.MiddleNameVal);
            const lastName = required(this.LastNameVal);
            const address = required(this.FullAddressVal);
            const serviceNo = required(this.ServiceNoVal);
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const phonePattern = /^\+?[0-9\s().-]{7,20}$/;

            if (!title) errors.title = "Title is required.";
            else if (title.length > 20) errors.title = "Title cannot exceed 20 characters.";
            if (!firstName) errors.firstName = "First name is required.";
            else if (firstName.length > 80) errors.firstName = "First name cannot exceed 80 characters.";
            if (!middleName) {
                errors.middleName = "Other name is required.";
            } else if (middleName.length > 80) errors.middleName = "Other name cannot exceed 80 characters.";
            if (!lastName) errors.lastName = "Last name is required.";
            else if (lastName.length > 80) errors.lastName = "Last name cannot exceed 80 characters.";

            if (!email) {
                errors.email = "Email address is required.";
            } else if (email.length > 254 || !emailPattern.test(email)) {
                errors.email = "Enter a valid email address.";
            }

            if (!phone) {
                errors.phone = "Phone number is required.";
            } else if (!phonePattern.test(phone)) {
                errors.phone = "Enter a valid phone number.";
            }

            if (!address) errors.fullAddress = "Address is required.";
            else if (address.length > 500) errors.fullAddress = "Address cannot exceed 500 characters.";
            if (!serviceNo) errors.serviceNo = "Service number is required.";
            else if (serviceNo.length > 50) errors.serviceNo = "Service number cannot exceed 50 characters.";

            const passwordRequired =
                !this.isModified || (this.editingPending && this.editingPendingCreate);
            if (passwordRequired) {
                if (!this.UserPasswordVal) {
                    errors.password = "Password is required for a new staff member.";
                } else if (this.UserPasswordVal.length < 8) {
                    errors.password = "Password must be at least 8 characters long.";
                }
            } else if (this.UserPasswordVal && this.UserPasswordVal.length < 8) {
                errors.password = "Password must be at least 8 characters long.";
            }

            if (![14, 15].includes(Number(this.CategoryIdVal))) errors.categoryId = "Choose a valid staff type.";
            if (![10, 11].includes(Number(this.SexIdVal))) errors.sexId = "Choose a valid sex.";
            if (![12, 13].includes(Number(this.MaritalStatusIdVal))) {
                errors.maritalStatusId = "Choose the marital status.";
            }

            if (!this.DOBval) {
                errors.dob = "Date of birth is required.";
            } else {
                const dob = String(this.DOBval).trim();
                const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
                let isValid = false;
                if (match) {
                    const year = Number(match[1]);
                    const month = Number(match[2]);
                    const day = Number(match[3]);
                    const parsed = new Date(Date.UTC(year, month - 1, day));
                    isValid =
                        parsed.getUTCFullYear() === year &&
                        parsed.getUTCMonth() === month - 1 &&
                        parsed.getUTCDate() === day;
                }
                if (!isValid) {
                    errors.dob = "Enter a valid date of birth.";
                } else if (dob >= this.getTodayDate) {
                    errors.dob = "Date of birth must be in the past.";
                }
            }

            this.staffErrors = errors;

            if (Object.keys(errors).length) {
                this.showErrorAlert(Object.values(errors));
                const fieldIds = {
                    title: "staff-title",
                    firstName: "staff-first-name",
                    middleName: "staff-middle-name",
                    lastName: "staff-last-name",
                    email: "staff-email",
                    phone: "staff-phone",
                    fullAddress: "staff-address",
                    serviceNo: "staff-service-no",
                    password: "staff-password",
                    categoryId: "staff-category",
                    sexId: "staff-sex",
                    maritalStatusId: "staff-marital",
                    dob: "staff-dob"
                };
                const firstField = Object.keys(errors)[0];
                const element = document.getElementById(fieldIds[firstField]);
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async GetServiceNo() {
            try {
                const resp = await this.$store.dispatch("getserviceno");
                const value =
                    typeof resp === "string"
                        ? resp
                        : resp && (resp.data || resp.serviceNo || resp.ServiceNo);
                if (value && !String(this.ServiceNoVal || "").trim()) {
                    this.ServiceNoVal = String(value);
                }
            } catch (error) {
                this.ShowAlertWarning(
                    "Could not generate a service number automatically. Enter one manually.",
                    ""
                );
            }
        },
        async SubmitStaffDetails() {
            if (this.staffSaving) return;
            if (!this.validateStaffForm()) return;

            const isEdit = Boolean(this.IdVal);
            const payload = {
                Id: isEdit ? this.IdVal : 0,
                ServiceNo: String(this.ServiceNoVal || "").trim(),
                Title: String(this.TitleVal || "").trim(),
                FirstName: String(this.FirstNameVal || "").trim(),
                MiddleName: String(this.MiddleNameVal || "").trim(),
                LastName: String(this.LastNameVal || "").trim(),
                PhoneNumber: String(this.PhoneNoVal || "").trim(),
                EmailAddress: String(this.EmailAddressVal || "").trim(),
                Passport: this.ImgPathval || "",
                SexId: Number(this.SexIdVal),
                MaritalStatusId: Number(this.MaritalStatusIdVal) || 0,
                CategoryId: Number(this.CategoryIdVal),
                IsPrincipal: Boolean(this.IsPrincipalVal),
                IsBursal: Boolean(this.IsBursalVal),
                IsPortalAdmin: Boolean(this.IsPortalAdminVal),
                IsHostelMaster: Boolean(this.IsHostelMasterVal),
                IsLibraryAdmin: Boolean(this.IsLibraryAdminVal),
                IsCounselor: Boolean(this.IsCounselorVal),
                IsRegistrar: Boolean(this.IsRegistrarVal),
                IsHOD: Boolean(this.IsHODVal),
                IsDailyAttendanceManager: Boolean(this.IsDailyAttendanceManagerVal),
                FullAddress: String(this.FullAddressVal || "").trim(),
                DOB: this.DOBval || ""
            };
            if (this.UserPasswordVal) payload.UserPassword = this.UserPasswordVal;

            this.staffSaving = true;
            this.onloader = "flex";
            try {
                if (isEdit && this.editingPending) {
                    await this.savePendingStaffEdit(payload);
                    return;
                }

                if (!isEdit) {
                    const localStaff = await staffRepository.create(payload);
                    this.handleBulkStaffCreated(localStaff);
                    this.activeTab = "active";
                    this.searchval = "";
                    this.CloseStaffForm();
                    this.ShowAlertSuccess(
                        "Staff saved; background synchronization queued",
                        ""
                    );
                } else {
                    const resp = await this.$store.dispatch(
                        "SchoolStaff/UpdateSchoolStaff",
                        payload
                    );
                    if (!resp || resp.responseCode !== "00") {
                        this.ShowAlertError(
                            (resp && resp.responseMessage) || "The staff record could not be saved.",
                            ""
                        );
                        return;
                    }
                    await this.GetSchoolStaffs();
                    this.CloseStaffForm();
                    this.ShowAlertSuccess("Staff record updated successfully.", "");
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this staff record at the moment."),
                    ""
                );
            } finally {
                this.staffSaving = false;
                this.onloader = "none";
            }
        },
        async savePendingStaffEdit(payload) {
            const clientId = String(this.editingClientId || this.IdVal || "").replace(
                /^staff:/,
                ""
            );
            if (!clientId) {
                throw new Error("This pending record has no local identifier.");
            }

            const entityKey = getOfflineEntityId("staff", clientId);
            const db = await getDB();
            const localDoc = await db.offlineEntities.findOne(entityKey).exec();
            const localData = localDoc ? localDoc.toJSON() : null;
            const operations = await db.syncOperations
                .find({ selector: { entityKey } })
                .exec();

            if (operations.some(doc => doc.toJSON().syncStatus === "syncing")) {
                this.ShowAlertWarning(
                    "This staff change is currently syncing. Try again in a moment.",
                    ""
                );
                return;
            }

            const editableOps = operations.filter(doc => {
                const status = doc.toJSON().syncStatus;
                return status === "pending" || status === "failed";
            });
            const createOp = editableOps.find(doc =>
                /CreateSchoolStaff/i.test(String(doc.toJSON().url || ""))
            );
            const primary = createOp || editableOps[editableOps.length - 1] || null;
            const primaryUrl = primary ? String(primary.toJSON().url || "") : "";
            const localOperation = localData && localData.operation;
            let operationKind = "create";
            if (primaryUrl) {
                operationKind = /CreateSchoolStaff/i.test(primaryUrl) ? "create" : "update";
            } else if (localOperation) {
                operationKind = localOperation === "create" ? "create" : "update";
            }

            const operationPayload = { ...payload, clientId };
            if (operationKind === "create") operationPayload.Id = 0;

            const now = new Date().toISOString();
            if (localDoc) {
                const existingData = localData.data || {};
                await localDoc.atomicPatch({
                    data: { ...existingData, ...operationPayload },
                    operation: operationKind,
                    syncStatus: "pending",
                    syncError: null,
                    nextRetryAt: null,
                    clientUpdatedAt: now,
                    updatedAt: now
                });
            }

            if (primary) {
                await primary.atomicPatch({
                    payload: operationPayload,
                    syncStatus: "pending",
                    syncError: null,
                    nextRetryAt: null,
                    clientUpdatedAt: now,
                    updatedAt: now
                });
                for (const extra of editableOps) {
                    if (extra !== primary) await extra.remove();
                }
            } else if (localDoc) {
                if (operationKind === "update") {
                    await staffRepository.update(operationPayload);
                } else {
                    await staffRepository.create(operationPayload);
                }
            } else {
                throw new Error(
                    "No local record or queued upload was found for this staff member."
                );
            }

            await syncOperations();
            await this.GetSchoolStaffs();
            this.CloseStaffForm();
            this.ShowAlertSuccess(
                "Pending staff change updated and queued for synchronization.",
                ""
            );
        },
        async DeleteSchoolStaff(Id) {
            const staff = (this.staffs || []).find(item => String(item.id) === String(Id));
            if (!staff) return;
            if (staff.syncStatus && staff.syncStatus !== "synced") {
                this.ShowAlertWarning(
                    "This staff record is pending sync. Push pending changes before deleting it.",
                    ""
                );
                return;
            }

            const willDelete = !staff.isDeleted;
            const confirmed = await this.showSchooldockAlert({
                title: willDelete ? "Delete staff member?" : "Restore staff member?",
                msg: willDelete
                    ? `${staff.staffName || "This staff member"} will be moved to deleted staff.`
                    : `${staff.staffName || "This staff member"} will be restored to active staff.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: willDelete ? "#c23b4a" : "#1850b8"
            });
            if (!confirmed || !confirmed.isConfirmed) return;

            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("SchoolStaff/DeleteSchoolStaff", {
                    staffId: staff.id,
                    isDeleted: willDelete,
                    deletedComment: willDelete
                        ? "Deleted by user because it is no longer needed"
                        : "Restored by user"
                });
                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The staff record could not be updated.",
                        ""
                    );
                    return;
                }
                await this.GetSchoolStaffs();
                this.ShowAlertSuccess(
                    willDelete ? "Staff member deleted." : "Staff member restored.",
                    ""
                );
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to update this staff record."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        async DiscardPendingStaff(item) {
            if (!item) return;
            if (!item.syncStatus || item.syncStatus === "synced") return;

            const rawClientId = item.clientId || item.id;
            if (!rawClientId) {
                this.ShowAlertError(
                    "This pending record has no local identifier to remove.",
                    ""
                );
                return;
            }
            const clientId = String(rawClientId).replace(/^staff:/, "");

            const confirmed = await this.showSchooldockAlert({
                title: "Discard pending staff change?",
                msg: "This removes the local record and its queued upload. The change will never be sent to the backend.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#c23b4a"
            });
            if (!confirmed || !confirmed.isConfirmed) return;

            this.onloader = "flex";
            try {
                const db = await getDB();
                const entityKey = getOfflineEntityId("staff", clientId);
                const operations = await db.syncOperations
                    .find({ selector: { entityKey } })
                    .exec();

                if (operations.some(doc => doc.toJSON().syncStatus === "syncing")) {
                    this.ShowAlertWarning(
                        "This staff change is currently syncing. Try again in a moment.",
                        ""
                    );
                    return;
                }

                const localIds = new Set([entityKey]);
                for (const doc of operations) {
                    const data = doc.toJSON();
                    if (data.localRecordId) localIds.add(data.localRecordId);
                }

                for (const doc of operations) {
                    await doc.remove();
                }
                for (const localId of localIds) {
                    const localDoc = await db.offlineEntities.findOne(localId).exec();
                    if (localDoc) await localDoc.remove();
                }

                await this.GetSchoolStaffs();
                this.ShowAlertSuccess("Pending staff change removed.", "");
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to remove this pending staff change."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },

        /* ---------- Shared lists ---------- */

        async GetSchoolStaffs() {
            this.staffLoadCount += 1;
            try {
            const ownerAtStart = getCurrentUserScope();
            if (!ownerAtStart) {
                this.staffs = [];
                return;
            }
            let localStaffs = [];
            try {
                localStaffs = await staffRepository.list();
                if (getCurrentUserScope() !== ownerAtStart) return;
                this.staffs = (localStaffs || []).map(displayStaff);
            } catch (error) {
                console.warn("Could not load local staff records", error);
                if (getCurrentUserScope() === ownerAtStart) this.staffs = [];
            }

            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                if (!localStaffs.length) this.staffs = [];
                return;
            }

            try {
                const resp = await this.$store.dispatch("SchoolStaff/GetSchoolStaffAll");
                if (getCurrentUserScope() !== ownerAtStart) return;
                const remote = resp && Array.isArray(resp.data) ? resp.data : [];
                try {
                    await Promise.all(remote.map(item => staffRepository.cache(item)));
                    if (getCurrentUserScope() !== ownerAtStart) return;
                    const latest = await staffRepository.list();
                    this.staffs = latest.map(displayStaff);
                } catch (error) {
                    if (getCurrentUserScope() === ownerAtStart) {
                        this.staffs = remote.map(displayStaff);
                    }
                }
            } catch (error) {
                if (getCurrentUserScope() !== ownerAtStart) return;
                if (!localStaffs || !localStaffs.length) {
                    this.staffs = [];
                    this.ShowAlertError(
                        this.errorMessage(error, "Unable to load staff records."),
                        ""
                    );
                }
            }
            } finally {
                this.staffLoadCount = Math.max(0, this.staffLoadCount - 1);
            }
        },
        async getClass() {
            try {
                const resp = await this.$store.dispatch("getclass");
                this.schclass = Array.isArray(resp) ? resp : [];
            } catch (error) {
                this.schclass = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load the class list."),
                    ""
                );
            }
        },
        /* ---------- Class teacher ---------- */

        addClassTeacher(staffOrId) {
            const record = this.findStaffRecord(staffOrId);
            if (!record) {
                this.ShowAlertError(
                    "The selected staff record could not be found in the loaded list.",
                    ""
                );
                return;
            }
            if (record.syncStatus && record.syncStatus !== "synced") {
                this.ShowAlertWarning(
                    "This staff record is pending sync. Push pending changes before assigning a class.",
                    ""
                );
                return;
            }
            this.StaffIdval = record.id || record.staffId || record.StaffId || 0;
            this.staffName =
                record.staffName ||
                [record.title, record.firstName, record.lastName].filter(Boolean).join(" ");
            this.staffPassport = record.passport || record.Passport || "";
            this.ClassIdvalAssignClass = 0;
            this.OptionIdvalAssignClass = 0;
            this.ArmIdvalAssignClass = 0;
            this.classOptions = [];
            this.classArms = [];
            this.classTeacherErrors = EMPTY_ASSIGNMENT_ERRORS();
            this.classTeacherVisible = true;
        },
        closeClassTeacher() {
            this.classTeacherVisible = false;
            this.classTeacherErrors = EMPTY_ASSIGNMENT_ERRORS();
            this.classOptions = [];
            this.classArms = [];
            this.classOptionsLoading = false;
            this.classArmsLoading = false;
        },
        async getOptionForAssignClass() {
            this.OptionIdvalAssignClass = 0;
            this.ArmIdvalAssignClass = 0;
            this.classArms = [];
            this.classTeacherErrors = EMPTY_ASSIGNMENT_ERRORS();
            const classId = this.ClassIdvalAssignClass;
            if (!classId) {
                this.classOptions = [];
                return;
            }
            this.classOptionsLoading = true;
            try {
                const resp = await this.$store.dispatch("getoption", classId);
                this.classOptions = Array.isArray(resp) ? resp : [];
            } catch (error) {
                this.classOptions = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load options for this class."),
                    ""
                );
            } finally {
                this.classOptionsLoading = false;
            }
        },
        async getArmForAssignClass() {
            this.ArmIdvalAssignClass = 0;
            this.classTeacherErrors = EMPTY_ASSIGNMENT_ERRORS();
            const optionId = this.OptionIdvalAssignClass;
            if (!optionId) {
                this.classArms = [];
                return;
            }
            this.classArmsLoading = true;
            try {
                const resp = await this.$store.dispatch("getarm", optionId);
                this.classArms = Array.isArray(resp) ? resp : [];
            } catch (error) {
                this.classArms = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load arms for this option."),
                    ""
                );
            } finally {
                this.classArmsLoading = false;
            }
        },
        async SubmitAssignedClass() {
            if (this.classTeacherSaving) return;
            const errors = EMPTY_ASSIGNMENT_ERRORS();
            if (!this.ClassIdvalAssignClass) errors.classId = "Choose a class.";
            if (!this.OptionIdvalAssignClass) errors.optionId = "Choose an option.";
            if (!this.ArmIdvalAssignClass) errors.armId = "Choose an arm.";
            this.classTeacherErrors = errors;
            if (Object.keys(errors).some(key => errors[key])) return;

            this.classTeacherSaving = true;
            try {
                const resp = await this.$store.dispatch("SchoolStaff/CreateClassTeacher", {
                    staffId: this.StaffIdval,
                    classId: this.ClassIdvalAssignClass,
                    sectionId: this.ArmIdvalAssignClass
                });
                if (!resp || resp.responseCode !== "00") {
                    this.ShowAlertError(
                        (resp && resp.responseMessage) || "The class assignment could not be saved.",
                        ""
                    );
                    return;
                }
                await this.GetSchoolStaffs();
                this.classTeacherVisible = false;
                this.ShowAlertSuccess("Class assignment saved.", "");
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this class assignment."),
                    ""
                );
            } finally {
                this.classTeacherSaving = false;
            }
        },

        /* ---------- Subject teacher ---------- */

        addSubjectTeacher(staffOrId) {
            const record = this.findStaffRecord(staffOrId);
            if (!record) {
                this.ShowAlertError(
                    "The selected staff record could not be found in the loaded list.",
                    ""
                );
                return;
            }
            if (record.syncStatus && record.syncStatus !== "synced") {
                this.ShowAlertWarning(
                    "This staff record is pending sync. Push pending changes before assigning a subject.",
                    ""
                );
                return;
            }
            this.StaffIdval = record.id || record.staffId || record.StaffId || 0;
            this.staffName =
                record.staffName ||
                [record.title, record.firstName, record.lastName].filter(Boolean).join(" ");
            this.staffPassport = record.passport || record.Passport || "";
            this.ClassIdvalSubjectTeacher = 0;
            this.OptionIdvalSubjectTeacher = 0;
            this.SubjectIdval = 0;
            this.subjectOptions = [];
            this.subjectSubjects = [];
            this.subjectTeacherErrors = EMPTY_ASSIGNMENT_ERRORS();
            this.subjectTeacherVisible = true;
        },
        closeSubjectTeacher() {
            this.subjectTeacherVisible = false;
            this.subjectTeacherErrors = EMPTY_ASSIGNMENT_ERRORS();
            this.subjectOptions = [];
            this.subjectSubjects = [];
            this.subjectOptionsLoading = false;
            this.subjectSubjectsLoading = false;
        },
        async getOptionForSubjectTeacher() {
            this.OptionIdvalSubjectTeacher = 0;
            this.SubjectIdval = 0;
            this.subjectSubjects = [];
            this.subjectTeacherErrors = EMPTY_ASSIGNMENT_ERRORS();
            const classId = this.ClassIdvalSubjectTeacher;
            if (!classId) {
                this.subjectOptions = [];
                return;
            }
            this.subjectOptionsLoading = true;
            try {
                const resp = await this.$store.dispatch("getoption", classId);
                this.subjectOptions = Array.isArray(resp) ? resp : [];
            } catch (error) {
                this.subjectOptions = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load options for this class."),
                    ""
                );
            } finally {
                this.subjectOptionsLoading = false;
            }
        },
        async getschsubject() {
            this.SubjectIdval = 0;
            this.subjectTeacherErrors = EMPTY_ASSIGNMENT_ERRORS();
            const optionId = this.OptionIdvalSubjectTeacher;
            if (!optionId) {
                this.subjectSubjects = [];
                return;
            }
            this.subjectSubjectsLoading = true;
            try {
                const resp = await this.$store.dispatch("getschsubject", {
                    ClassId: this.ClassIdvalSubjectTeacher,
                    SchsubId: 1,
                    OptionId: optionId,
                    processop: "N",
                    username: this.$store.state.auth.username,
                    accesskey: this.$store.state.auth.accesskey
                });
                this.subjectSubjects = Array.isArray(resp) ? resp : [];
            } catch (error) {
                this.subjectSubjects = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load subjects for this option."),
                    ""
                );
            } finally {
                this.subjectSubjectsLoading = false;
            }
        },
        async SubmitSubjectTeacher() {
            if (this.subjectTeacherSaving) return;
            const errors = EMPTY_ASSIGNMENT_ERRORS();
            if (!this.ClassIdvalSubjectTeacher) errors.classId = "Choose a class.";
            if (!this.OptionIdvalSubjectTeacher) errors.optionId = "Choose an option.";
            if (!this.SubjectIdval) errors.subjectId = "Choose a subject.";
            this.subjectTeacherErrors = errors;
            if (Object.keys(errors).some(key => errors[key])) return;

            this.subjectTeacherSaving = true;
            try {
                const resp = await this.$store.dispatch("SchoolStaff/CreateSubjectTeacher", {
                    staffId: this.StaffIdval,
                    subjectId: this.SubjectIdval
                });
                if (!resp || resp.responseCode !== "00") {
                    this.ShowAlertError(
                        (resp && resp.responseMessage) || "The subject assignment could not be saved.",
                        ""
                    );
                    return;
                }
                await this.GetSchoolStaffs();
                this.subjectTeacherVisible = false;
                this.ShowAlertSuccess("Subject assignment saved.", "");
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this subject assignment."),
                    ""
                );
            } finally {
                this.subjectTeacherSaving = false;
            }
        },

        async deleteclassteacher(Id) {
            if (!Id) return;
            this.onloader = "flex";
            try {
                await this.$store.dispatch("deleteclassteacher", Id);
                await this.GetSchoolStaffs();
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to remove this class assignment."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        async deletesubjectteacher(Id) {
            if (!Id) return;
            this.onloader = "flex";
            try {
                await this.$store.dispatch("deletesubjectteacher", Id);
                await this.GetSchoolStaffs();
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to remove this subject assignment."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        async pushPendingStaffSync() {
            if (this.syncingPending) return;
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                this.ShowAlertWarning(
                    "You are offline. Reconnect before pushing pending staff changes.",
                    ""
                );
                return;
            }

            this.syncingPending = true;
            try {
                const db = await getDB();
                const collection =
                    db.syncOperations || (db.collections && db.collections.syncOperations);
                if (!collection) throw new Error("The offline queue is not available.");

                const docs = await collection
                    .find({ selector: { syncStatus: { $in: ["pending", "failed"] } } })
                    .exec();
                const staffDocs = docs.filter(doc => {
                    const data = doc.toJSON();
                    return data.ownerId === getCurrentUserScope() &&
                        String(data.entityKey || "").startsWith("staff:");
                });

                for (const doc of staffDocs) {
                    const data = doc.toJSON();
                    if (data.nextRetryAt) {
                        await doc.atomicPatch({ nextRetryAt: null });
                    }
                }

                if (staffDocs.length) {
                    await syncOperations();
                    await this.GetSchoolStaffs();

                    if (this.pendingSyncCount) {
                        await new Promise(resolve => setTimeout(resolve, 800));
                        await this.GetSchoolStaffs();
                    }

                    if (this.pendingSyncCount) {
                        this.ShowAlertWarning(
                            "Some pending staff changes could not be pushed yet. They will retry automatically.",
                            ""
                        );
                    } else {
                        this.ShowAlertSuccess(
                            `${staffDocs.length} pending staff change(s) pushed to the backend.`,
                            ""
                        );
                    }
                } else if (this.pendingSyncCount) {
                    await this.GetSchoolStaffs();
                    this.ShowAlertWarning(
                        "Local staff changes are pending, but no queued backend operation was found. They will retry automatically.",
                        ""
                    );
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to push pending staff changes."),
                    ""
                );
            } finally {
                this.syncingPending = false;
            }
        }
    },
    mounted() {
        this.getClass();
        this.GetSchoolStaffs().then(() => {
            if (
                this.pendingSyncCount &&
                (typeof navigator === "undefined" || navigator.onLine !== false)
            ) {
                this.pushPendingStaffSync();
            }
        });
        this.unsubscribeSynced = subscribeSynced(operation => {
            if (operation.ownerId === getCurrentUserScope() &&
                String(operation.entityKey || "").startsWith("staff:")) {
                this.GetSchoolStaffs();
            }
        });
    },
    beforeDestroy() {
        if (this.unsubscribeSynced) this.unsubscribeSynced();
    }
};
</script>

<style scoped>
.staff-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(430px, 1fr));
    gap: 1rem;
    padding: 1.25rem 1.5rem;
}

.staff-grid > * {
    min-width: 0;
}

.staff-grid ::v-deep .card {
    margin: 0 !important;
}

.staff-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.6rem;
}

.staff-actions .ws-stat {
    margin: 0;
}

.staff-actions .ws-ghost-button {
    white-space: nowrap;
}

.staff-tabs {
    display: inline-flex;
    gap: 0.2rem;
    padding: 0.2rem;
    border-radius: 0.55rem;
    background: #f1f5f9;
}

.staff-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.42rem 0.8rem;
    border: 0;
    border-radius: 0.45rem;
    background: transparent;
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.staff-tab:hover,
.staff-tab:focus {
    color: #1850b8;
    outline: none;
}

.staff-tab.is-active {
    background: #fff;
    color: #1850b8;
    box-shadow: 0 0.1rem 0.35rem rgba(15, 23, 42, 0.1);
}

.staff-tab__count {
    display: inline-flex;
    min-width: 1.25rem;
    justify-content: center;
    padding: 0.08rem 0.35rem;
    border-radius: 999px;
    background: #eef4ff;
    color: #2d6fae;
    font-size: 0.64rem;
    font-weight: 700;
}

.staff-tab.is-active .staff-tab__count {
    background: #eef4ff;
    color: #1850b8;
}

.staff-photo {
    display: grid;
    grid-template-columns: 130px minmax(0, 1fr);
    gap: 1rem;
    align-items: start;
    margin-bottom: 1rem;
}

.staff-photo__preview {
    display: grid;
    min-height: 130px;
    place-items: center;
    overflow: hidden;
    border: 1px solid #e5eaf0;
    border-radius: 0.7rem;
    background: #f8fafc;
    padding: 0.4rem;
}

.staff-photo__preview img {
    width: 100%;
    max-height: 160px;
    border-radius: 0.5rem;
    object-fit: cover;
}

.staff-photo__upload {
    min-width: 0;
}

.staff-roles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.55rem 0.85rem;
}

.staff-roles ::v-deep .custom-control {
    margin: 0;
}

.staff-roles ::v-deep .custom-control-label {
    color: #44566c;
    font-size: 0.8rem;
    font-weight: 600;
}

.assign-context {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.35rem;
    padding: 0.75rem 0.85rem;
    border: 1px solid #e5eaf0;
    border-radius: 0.7rem;
    background: #f8fafc;
}

.assign-context img {
    flex: 0 0 2.5rem;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: #eef4ff;
    object-fit: cover;
}

.assign-context div {
    display: grid;
    gap: 0.1rem;
    min-width: 0;
}

.assign-context strong {
    overflow-wrap: anywhere;
    color: #344767;
    font-size: 0.86rem;
    font-weight: 700;
}

.assign-context span {
    color: #8492a2;
    font-size: 0.72rem;
}

.staff-bulk {
    padding: 0.25rem 0.25rem 0.5rem;
}

.staff-bulk__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 0.85rem;
    border-bottom: 1px solid #edf0f3;
    margin-bottom: 0.85rem;
}

.staff-bulk__eyebrow {
    display: block;
    margin-bottom: 0.15rem;
    color: #5b86b5;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.1em;
}

.staff-bulk__head h3 {
    margin: 0;
    color: #344767;
    font-size: 1.1rem;
    font-weight: 700;
}

.staff-bulk__head p {
    margin: 0.25rem 0 0;
    color: #7d8b9a;
    font-size: 0.78rem;
}

.staff-bulk .ws-drawer__close {
    flex: 0 0 2rem;
}

@media (max-width: 1199.98px) {
    .staff-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}

@media (max-width: 991.98px) {
    .staff-actions {
        justify-content: flex-start;
    }
}

@media (max-width: 767.98px) {
    .staff-tabs {
        justify-content: center;
    }
}

@media (max-width: 575.98px) {
    .staff-grid {
        padding: 0.85rem;
        gap: 0.75rem;
    }

    .staff-actions {
        width: 100%;
    }

    .staff-actions .ws-stat {
        flex: 1 1 100%;
    }

    .staff-actions .ws-primary-button,
    .staff-actions .ws-ghost-button {
        flex: 1 1 auto;
        justify-content: center;
    }

    .staff-photo {
        grid-template-columns: minmax(0, 1fr);
    }

    .staff-photo__preview {
        min-height: 110px;
    }

    .staff-photo__preview img {
        max-height: 180px;
    }

    .staff-roles {
        grid-template-columns: minmax(0, 1fr);
    }

    .staff-bulk__head {
        flex-direction: column;
    }
}
</style>

<style>
.modal-dialog.modal-xl {
    max-width: 95vw !important;
}

.bulk-upload-modal-content {
    max-height: 94vh;
    overflow-y: auto;
}

.staff-bulk .btn-success {
    background-color: #1850b8 !important;
    border-color: #1850b8 !important;
    color: #fff !important;
}

.staff-bulk .btn-success:hover,
.staff-bulk .btn-success:focus {
    background-color: #144a9e !important;
    border-color: #144a9e !important;
}
</style>
